import EF from 'ef/ef';
import Chaplin from 'chaplin';
import Page from 'ef/models/page';
import _ from 'lodash';

// # EF.Collections.PagesCollection
//
// the PagesCollection is responsible for managing a set of pages,
// keeping them in order and paging through them.
//
const PagesCollection = Chaplin.Collection.extend({
  model: Page,

  modelId: function (attrs) {
    return attrs.key;
  },

  initialize: function (models, options) {
    this.options = options || {};
    this.module = options && options.module;
    this.activity = options && options.activity;

    EF.on('page:completed', page => {
      if (page.module === this.module && page.activity === this.activity) {
        if (this.isLastPage()) {
          page.module.completeAllPages();
        } else if (page.get('optional')) {
          page.module.saveOptionalPage(page);
        } else {
          let index = this.indexOf(page) - 1;
          while (index > 0 && this.at(index) && this.at(index).get('optional')) {
            this.at(index).set('completed', true);
            this.at(index).module.setLastCompletedPage(this.at(index));
            index--;
          }
          if (index > 0 && index < this.indexOf(page) - 1) {
            this.at(index).set('completed', true);
            this.at(index).module.setLastCompletedPage(this.at(index));
          }
        }
      }

      if (!page.get('completedEventSent') && EF.modules.firstIncomplete() === undefined) {
        let completed;
        if (typeof CustomEvent === 'function') {
          completed = new CustomEvent('content:complete');
        } else {
          // IE11
          completed = document.createEvent('CustomEvent');
          completed.initCustomEvent('content:complete', true, true, 'content completed');
        }
        page.set('completedEventSent', true);
        window.dispatchEvent(completed);
      }
    });
  },
  // Returns the start page
  startPage: function (options) {
    if (options.startPage) {
      return this.getByKey(options.startPage);
    } else {
      return this.head();
    }
  },

  // returns page by key
  getPageByKey: function (key) {
    return this.find(function (page) {
      return page.get('key') === key;
    });
  },

  // returns page for a given index
  getPageByIndex: function (idx) {
    return this.find(function (page) {
      return page.get('index') === idx;
    });
  },

  // Override the comparator to sort the pages by index
  comparator: function (page) {
    return page.get('index');
  },

  // Returns the next page by index.
  // if a page is passed in, it returns the next one by index,
  // if a page is not passed in, if the activity is complete it returns the
  // first page by index, else it returns the first page that has not been
  // completed.
  next: function (page) {
    if (!page) {
      if (this.activity.isComplete() && !this.activity.get('redo')) {
        this.head();
      } else {
        return this.firstNotCompleted();
      }
    }

    const idx = page.get('index');
    const model = this.find(function (p) {
      return p.get('index') > idx;
    });
    if (model) {
      return model;
    } else {
      return false;
    }
  },

  firstNotCompleted: function () {
    let firstOptional,
      index = 0,
      nextPage,
      gotoPage;
    const self = this;
    const last = this.find(function (page, i) {
      // We want to skip optional pages here, and only set them complete
      // when the page following a set of optional pages are complete
      nextPage = self.at(i + 1);
      if (!firstOptional && nextPage && nextPage.get('optional') && !nextPage.get('completed')) {
        firstOptional = page;
        nextPage.set('completed', false);
        nextPage.module.setLastCompletedPage(nextPage);
        index++;
        while (self.at(i + index).get('optional')) {
          self.at(i + index).set('completed', false);
          self.at(i + index).module.setLastCompletedPage(self.at(i + index));
          index++;
        }
        gotoPage = self.at(i + index);
        index = 0;
        if (gotoPage.get('completed')) {
          firstOptional = undefined;
          return false;
        }
      } else {
        return !page.get('completed');
      }
    });
    return firstOptional || last;
  },

  lastCompleted: function () {
    let lastCompletePage = this.head();
    this.find(page => {
      if (page.get('optional')) {
        const completedOptionalPages = page.module.getCompletedOptionalPages();
        const completedPath = completedOptionalPages?.slice(-1)[0];
        const pagePath = page.path();

        if (completedPath === pagePath) {
          lastCompletePage = page;
        } else {
          return false;
        }
      } else if (page.get('completed')) {
        lastCompletePage = page;
      } else {
        return true;
      }
    });
    return lastCompletePage;
  },

  // Returns the next page by index.
  // More specifically returns the next page with an index that is less than
  // the page passed in.
  back: function (page) {
    const idx = page.get('index');
    const models = this.toArray().reverse();
    const model = _.find(models, function (p) {
      return p.get('index') < idx;
    });
    if (model) {
      return model;
    } else {
      return false;
    }
  },

  notCompleted: function () {
    return this.filter(function (page) {
      return !page.get('completed');
    });
  },
  isLastPage: function () {
    const currentModuleLastActivity = EF.module?.activities.at(EF.module.activities.length - 1);
    const lastPage = currentModuleLastActivity?.pages.at(currentModuleLastActivity.pages.length - 1);

    const currentPage = EF.module?.currentActivity.currentPage;
    if (!lastPage || !currentPage) {
      return false;
    }

    return lastPage?.id === currentPage?.id && lastPage?.get('activityKey') === currentPage?.get('activityKey');
  },
});

export default PagesCollection;
